import { ChangeDetectionStrategy, Component } from '@angular/core';
import { forkJoin } from 'rxjs';
import { CompanyVendor } from 'src/app/classes/models';
import { WidgetPanelCounts } from 'src/app/interfaces/widget-panel-counts';
import { CompanyVendorService } from 'src/app/services/company-vendor.service';
import { UtilityService } from 'src/app/services/utility.service';

@Component({
  selector: 'app-quality-control-widget',
  templateUrl: './quality-control-widget.component.html',
  styleUrls: ['./quality-control-widget.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class QualityControlWidgetComponent {
  qualityControls: WidgetPanelCounts['data'] = new Map([]);

  widgetPanelCounts: WidgetPanelCounts = {
    title: 'Quality Control',
    hasData: false,
    data: this.qualityControls
  };

  getCountOfVendorsWithFeeWaiverRemovedAndNoRenewalDate: {} = {
    viveStatus: {
      $notIn: [
        'Inactive',
        'Suspended',
        'Registering',
        'Awaiting Payment',
        'Business Verified',
        'Non-Verified',
        'Compliance Exempt'
      ]
    },
    feeWaived: false,
    vendorPaid: false,
    unFeeWaivedDate: { $ne: null },
    vendorLastDatePaid: null,
    feeWaivedDate: null,
    lastRenewalAnniversaryDate: null,
    lastFeeWaivedDate: null
  };

  getCountOfApprovedVendorsWithExpiredPayment: {} = {
    viveStatus: 'Approved',
    feeWaived: false,
    literals: {
      $and: ['approvedVendorsWithExpiredPayment']
    }
  };

  getBusinessInfoStepFindingMismatch: {} = {
    viveStatus: JSON.stringify({ $eq: 'Approved' }),
    literals: JSON.stringify({
      $and: [
        'businessComplianceSettingOrComplianceSettingIdNotNull',
        'businessInfoStepFindingMismatch'
      ]
    }),
    Company: JSON.stringify({
      options: { required: true },
      status: { $ne: 'deactivated' },
      BusinessComplianceSetting: {
        options: { required: false, duplicating: false },
        literals: {
          CompanyComplianceTypeId: { $eq: 'companyVendorComplianceTypeId' }
        }
      },
      ComplianceSetting: {
        options: { required: false, duplicating: false },
        literals: {
          CompanyComplianceTypeId: { $eq: 'companyVendorComplianceTypeId' },
          riskLevel: { $eq: 'selectMaxRiskLevelForCompanyVendorComplianceType' }
        }
      }
    }),
    Vendor: JSON.stringify({
      options: { required: true },
      ComplianceFinding: {
        options: { required: true }
      }
    }),
    ViveProgress: JSON.stringify({
      options: { required: true, duplicating: false },
      type: 'Vendor Info',
      status: 'Passed',
      subStatus: {
        $or: [{ $ne: 'Waived Findings' }, null]
      }
    })
  };

  getOwnerInfoStepFindingMismatch: {} = {
    viveStatus: { $eq: 'Approved' },
    literals: {
      $and: [
        'fullServiceAndOnSiteOrFullServiceAndNotOnSite',
        'ownerInfoStepFindingMisMatch'
      ]
    },
    Company: {
      options: { required: true, duplicating: false },
      status: { $ne: 'deactivated' },
      ComplianceSetting: {
        options: { required: false, duplicating: false },
        literals: {
          CompanyComplianceTypeId: { $eq: 'companyVendorComplianceTypeId' },
          riskLevel: { $eq: 'selectMaxRiskLevelForCompanyVendorComplianceType' }
        }
      }
    },
    Vendor: {
      options: { required: true }
    },
    ViveProgress: {
      options: { required: true, duplicating: false },
      type: 'Owner Info',
      status: 'Passed',
      subStatus: {
        $or: [{ $ne: 'Waived Findings' }, null]
      }
    }
  };

  getTradesAndLicensesInfoStepFindingMismatch: {} = {
    viveStatus: { $eq: 'Approved' },
    Company: {
      options: { required: true },
      status: { $ne: 'deactivated' }
    },
    Vendor: {
      options: { required: true }
    },
    ViveProgress: {
      options: { required: true, duplicating: false },
      type: 'Trades & Licenses',
      status: 'Passed',
      subStatus: {
        $or: [{ $ne: 'Waived Findings' }, null]
      }
    },
    literals: {
      $and: ['tradesAndLicensesInfoStepFindingMismatch']
    }
  };

  getCountOfHangingRequestJobs: {} = {
    ExemptVendorId: { $eq: null },
    $or: [
      { ClientRegisteredVendorId: { $ne: null } },
      { VendorId: { $ne: null } }
    ],
    literals: {
      $and: ['vendorStuckInBusinessVerification']
    }
  };

  get getCountOfVendorsMovedToRegisteredFromSiteConditionalOrApprovedWaiverOrSuspended() {
    return this.CompanyVendorService.getCountOfVendorsMovedToRegisteredFromSiteConditionalOrApprovedWaiverOrSuspended();
  }

  constructor(
    private CompanyVendorService: CompanyVendorService,
    private UtilityService: UtilityService
  ) {
    forkJoin([
      this.getCompanyVendorCount(
        this.getCountsByStatusAndTime('Registering', 1)
      ),
      this.getCompanyVendorCount(this.getCountOfVendorsNotStartedReview()),
      this.getCompanyVendorCount(
        this.getCountOfApprovedVendors('failedViveProgress')
      ),
      this.getCompanyVendorCount(
        this.getCountOfApprovedVendors('viveProgressReviewingAndResolving')
      ),
      this.getCompanyVendorCount(
        this.getCountOfSuspendedVendorsWithPassesOrWaivers()
      ),
      this.getCompanyVendorCount(this.getCountOfVendorsStuckInReviewing(30)),
      this.getCompanyVendorCount(this.getCountOfVendorsStuckInReviewing(45)),
      this.getCompanyVendorCount(this.getCountOfVendorsStuckInReviewing(60)),
      this.getCompanyVendorCount(this.getCountOfVendorsWithInvoicePending()),
      this.getCompanyVendorCount(
        this.getCountOfVendorsWithFeeWaiverRemovedAndNoRenewalDate
      ),
      this.getCompanyVendorCount(
        this.getCountOfApprovedVendorsWithExpiredPayment
      ),
      this
        .getCountOfVendorsMovedToRegisteredFromSiteConditionalOrApprovedWaiverOrSuspended,
      this.getCompanyVendorCount(this.getBusinessInfoStepFindingMismatch),
      this.getCompanyVendorCount(this.getOwnerInfoStepFindingMismatch),
      this.getCompanyVendorCount(
        this.getTradesAndLicensesInfoStepFindingMismatch
      ),
      this.getCompanyVendorCount(this.getCountOfHangingRequestJobs)
    ]).subscribe({
      next: ([
        countOfVendorsInRegisteredAtLeast2Days,
        countOfVendorsNotStartedReview,
        countOfApprovedVendorsWithFailsAndNoWaivers,
        getCountOfApprovedVendorsInReviewingOrResolving,
        getCountOfSuspendedVendorsWithPassesOrWaivers,
        getCountOfVendorsStuckInReviewing30,
        getCountOfVendorsStuckInReviewing45,
        getCountOfVendorsStuckInReviewing60,
        getCountOfVendorsWithInvoicePending,
        getCountOfVendorsWithFeeWaiverRemovedAndNoRenewalDate,
        getCountOfApprovedVendorsWithExpiredPayment,
        getCountOfVendorsMovedToRegisteredFromSiteConditionalOrApprovedWaiverOrSuspended,
        getBusinessInfoStepFindingMismatch,
        getOwnerInfoStepFindingMismatch,
        getTradesAndLicensesInfoStepFindingMismatch,
        getCountOfHangingRequestJobs
      ]) => {
        this.qualityControls.set('Registering 1 Day or Longer', {
          count: Number(countOfVendorsInRegisteredAtLeast2Days),
          query: this.getCountsByStatusAndTime('Registering', 1)
        });
        this.qualityControls.set('Vendor Reviews Not Started', {
          count: Number(countOfVendorsNotStartedReview),
          query: this.getCountOfVendorsNotStartedReview()
        });
        this.qualityControls.set('Vendors Approved with Fails & No Waiver', {
          count: Number(countOfApprovedVendorsWithFailsAndNoWaivers),
          query: this.getCountOfApprovedVendors('failedViveProgress')
        });
        this.qualityControls.set('Vendors Approved in Reviewing or Resolving', {
          count: Number(getCountOfApprovedVendorsInReviewingOrResolving),
          query: this.getCountOfApprovedVendors(
            'viveProgressReviewingAndResolving'
          )
        });
        this.qualityControls.set('Vendors Suspended with Passes or Waiver', {
          count: Number(getCountOfSuspendedVendorsWithPassesOrWaivers),
          query: this.getCountOfSuspendedVendorsWithPassesOrWaivers()
        });
        this.qualityControls.set('In Reviewing 30 Days', {
          count: Number(
            getCountOfVendorsStuckInReviewing30 -
              getCountOfVendorsStuckInReviewing45
          ),
          query: this.getCountOfVendorsStuckInReviewing(30)
        });
        this.qualityControls.set('In Reviewing 45 Days', {
          count: Number(
            getCountOfVendorsStuckInReviewing45 -
              getCountOfVendorsStuckInReviewing60
          ),
          query: this.getCountOfVendorsStuckInReviewing(45)
        });
        this.qualityControls.set('In Reviewing 60 Days', {
          count: Number(getCountOfVendorsStuckInReviewing60),
          query: this.getCountOfVendorsStuckInReviewing(60)
        });
        this.qualityControls.set('Invoice Pending 24 Hours or Longer', {
          count: Number(getCountOfVendorsWithInvoicePending),
          query: this.getCountOfVendorsWithInvoicePending()
        });
        this.qualityControls.set('Fee Voucher Removed and No Renewal Date', {
          count: Number(getCountOfVendorsWithFeeWaiverRemovedAndNoRenewalDate),
          query: this.getCountOfVendorsWithFeeWaiverRemovedAndNoRenewalDate
        });
        this.qualityControls.set('Approved With Expired Payment', {
          count: Number(getCountOfApprovedVendorsWithExpiredPayment),
          query: this.getCountOfApprovedVendorsWithExpiredPayment
        });
        this.qualityControls.set(
          'Review Insurance for Site Conditional Vendors',
          {
            count: Number(
              getCountOfVendorsMovedToRegisteredFromSiteConditionalOrApprovedWaiverOrSuspended
            )
          }
        );
        this.qualityControls.set(
          'Unwaived Business Passes With Fails/Missing',
          {
            count: Number(getBusinessInfoStepFindingMismatch),
            query: this.getBusinessInfoStepFindingMismatch
          }
        );
        this.qualityControls.set('Unwaived Owner Passes With Fails/Missing', {
          count: Number(getOwnerInfoStepFindingMismatch),
          query: this.getOwnerInfoStepFindingMismatch
        });
        this.qualityControls.set('Unwaived License Passes With Missing Info', {
          count: Number(getTradesAndLicensesInfoStepFindingMismatch),
          query: this.getTradesAndLicensesInfoStepFindingMismatch
        });
        this.qualityControls.set('Vendors Stuck In Business Verification', {
          count: Number(getCountOfHangingRequestJobs),
          query: this.getCountOfHangingRequestJobs
        });
      }
    });
  }

  private getCountOfVendorsWithInvoicePending() {
    const query = {
      viveStatus: { $ne: 'Inactive' },
      literals: {
        $and: ['companyVendorsInvoicesPending']
      }
    };

    return this.UtilityService.stringifyObjectsInQueryObject(query);
  }

  private getCountOfVendorsStuckInReviewing(numberOfDays: number) {
    const query = {
      viveStatus: 'Reviewing',
      statusDate: {
        $lte: (() => {
          const date = new Date();
          date.setDate(date.getDate() - numberOfDays);

          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');

          return new Date(`${year}-${month}-${day} 23:59:59`);
        })()
      }
    };

    return this.UtilityService.stringifyObjectsInQueryObject(query);
  }

  private getCountOfSuspendedVendorsWithPassesOrWaivers() {
    const query = {
      viveStatus: 'Suspended',
      viveSubStatus: {
        $or: [
          {
            $notIn: ['Expired Insurance', 'No Renewal', 'Fee Waived No Renewal']
          },
          { $eq: null }
        ]
      },
      Company: {},
      Vendor: {},
      literals: {
        $and: ['companyVendorFullServiceOnsiteIsOnSite']
      },
      BusinessComplianceProgress: {
        options: { required: true },
        status: 'Passed'
      },
      OwnerComplianceProgress: {
        options: { required: false }
      },
      TradeLicenseComplianceProgress: {
        options: { required: true },
        status: 'Passed'
      },
      InsuranceComplianceProgress: {
        options: { required: true },
        status: 'Passed'
      }
    };

    return this.UtilityService.stringifyObjectsInQueryObject(query);
  }

  private getCountOfApprovedVendors(
    viveProgressLiteral:
      'viveProgressReviewingAndResolving' | 'failedViveProgress'
  ) {
    const query = {
      viveStatus: {
        $in: ['Approved', 'Site Conditional']
      },
      literals: {
        $and: [viveProgressLiteral]
      },
      Company: {
        attributes: ['id', 'fullServiceOnsite', 'fullServiceOffsite'],
        options: { required: true }
      },
      Vendor: {
        attributes: ['id', 'isOnSite'],
        options: { required: true }
      }
    };

    return this.UtilityService.stringifyObjectsInQueryObject(query);
  }

  private getCountOfVendorsNotStartedReview() {
    const query = {
      viveStatus: {
        $and: [
          { $in: ['Renewing', 'Registered', 'Reviewing'] },
          {
            $notIn: [
              'Inactive',
              'Suspended',
              'Registering',
              'Awaiting Payment',
              'Compliance Exempt'
            ]
          }
        ]
      },
      literals: {
        $and: ['noViveProgress']
      }
    };
    return this.UtilityService.stringifyObjectsInQueryObject(query);
  }

  private getCountsByStatusAndTime(
    status: CompanyVendor['viveStatus'],
    time: number
  ) {
    const query = {
      viveStatus: status,
      statusDate: {
        $lte: (() => {
          const now = new Date();
          const utcNow = new Date(now.toISOString());

          return new Date(utcNow.getTime() - time * 24 * 60 * 60 * 1000);
        })()
      }
    };

    return this.UtilityService.stringifyObjectsInQueryObject(query);
  }

  private getCompanyVendorCount(query: {}) {
    return this.CompanyVendorService.getCompanyVendorCount(
      this.UtilityService.stringifyObjectsInQueryObject(query)
    );
  }
}
